import React, { useState } from 'react';
import { Radio, Hash, Plus, Play, Pause, Trash2, MessageSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PC, PL, ALL_PLATFORMS, StreamConfig } from './types';

const INTERVAL_OPTIONS = [
  { value: 15, label: 'Every 15 min' },
  { value: 30, label: 'Every 30 min' },
  { value: 60, label: 'Every hour' },
  { value: 180, label: 'Every 3 hours' },
  { value: 360, label: 'Every 6 hours' },
];

function expiryLabel(expiresAt?: string): { text: string; color: string } | null {
  if (!expiresAt) return null;
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return { text: 'Expired', color: '#ef4444' };
  const hoursLeft = msLeft / 3_600_000;
  if (hoursLeft < 2) return { text: `Expires in ${Math.round(hoursLeft * 60)}m`, color: '#ef4444' };
  return { text: `Expires in ${Math.round(hoursLeft)}h`, color: hoursLeft < 6 ? '#f97316' : '#4b5563' };
}

interface StreamSidebarProps {
  streams: StreamConfig[];
  onToggle: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
  onSelect: (s: StreamConfig) => void;
  onAdd: (data: { keyword: string; platforms: string[]; fetch_interval_minutes: number; email_alerts_enabled: boolean }) => void;
  adding: boolean;
  platformBreakdown: Array<[string, number]>;
  totalPosts: number;
}

export const StreamSidebar: React.FC<StreamSidebarProps> = ({
  streams, onToggle, onDelete, onSelect, onAdd, adding, platformBreakdown, totalPosts,
}) => {
  const [addKeyword, setAddKeyword] = useState('');
  const [addPlatforms, setAddPlatforms] = useState<string[]>(['twitter', 'instagram', 'facebook']);
  const [addInterval, setAddInterval] = useState(60);
  const [addEmailAlerts, setAddEmailAlerts] = useState(true);

  const submit = () => {
    if (!addKeyword.trim()) return;
    onAdd({ keyword: addKeyword.trim(), platforms: addPlatforms, fetch_interval_minutes: addInterval, email_alerts_enabled: addEmailAlerts });
    setAddKeyword('');
  };

  return (
    <div className="w-64 flex-shrink-0 flex flex-col overflow-hidden" style={{ borderRight: '1px solid #1a1a1a', background: '#0a0a0a' }}>
      <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid #1a1a1a' }}>
        <div className="flex items-center gap-2">
          <Radio size={14} style={{ color: '#00ff00' }} className="animate-pulse" />
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: '#00ff00' }}>Keyword Monitor</span>
        </div>
        <p className="text-[10px] text-gray-600 mt-1">{streams.length} streams · {streams.filter(s => s.is_active).length} active</p>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {streams.length === 0 && (
          <div className="text-center py-8">
            <Hash size={24} className="mx-auto text-gray-700 mb-2" />
            <p className="text-[11px] text-gray-600">No streams yet.</p>
            <p className="text-[10px] text-gray-700 mt-1">Add a keyword below, or check "monitor this" on a search.</p>
          </div>
        )}
        {streams.map(s => {
          const expiry = expiryLabel(s.expires_at);
          return (
            <div key={s.id}
              className="rounded-lg p-3 cursor-pointer transition-all hover:brightness-125 group"
              style={{ background: '#111', border: `1px solid ${s.is_active ? '#00ff0020' : '#1a1a1a'}` }}
              onClick={() => onSelect(s)}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-semibold text-white truncate max-w-[120px]">{s.keyword}</span>
                <div className="flex items-center gap-1">
                  <button onClick={e => { e.stopPropagation(); onToggle(s.id, !s.is_active); }}
                    className="p-0.5 rounded hover:bg-white/10">
                    {s.is_active ? <Pause size={10} style={{ color: '#00ff00' }} /> : <Play size={10} className="text-gray-500" />}
                  </button>
                  <button onClick={e => { e.stopPropagation(); onDelete(s.id); }}
                    className="p-0.5 rounded hover:bg-red-900/30">
                    <Trash2 size={10} className="text-gray-600 hover:text-red-400" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {s.platforms.map(p => (
                  <span key={p} className="text-[9px] px-1 py-0.5 rounded" style={{ background: `${PC[p] ?? PC.default}20`, color: PC[p] ?? PC.default }}>
                    {PL[p] ?? p}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500 flex items-center gap-1">
                  <MessageSquare size={9} /> {s.total_posts ?? 0} posts
                </span>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: s.is_active ? '#00ff00' : '#374151', boxShadow: s.is_active ? '0 0 6px #00ff00' : 'none' }} />
              </div>
              {expiry && (
                <p className="text-[9px] font-mono mt-1" style={{ color: expiry.color }}>{expiry.text}</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-3 flex-shrink-0 space-y-2" style={{ borderTop: '1px solid #1a1a1a' }}>
        <p className="text-[10px] font-mono text-gray-600 uppercase tracking-wider">Monitor keyword</p>
        <Input
          placeholder="keyword..."
          value={addKeyword}
          onChange={e => setAddKeyword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          className="h-8 text-xs"
          style={{ background: '#111', border: '1px solid #222', color: '#e5e7eb' }}
        />
        <div className="flex flex-wrap gap-1">
          {ALL_PLATFORMS.map(p => (
            <button key={p} onClick={() => setAddPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
              className="text-[9px] px-1.5 py-0.5 rounded transition-all"
              style={{
                background: addPlatforms.includes(p) ? `${PC[p]}25` : '#111',
                color: addPlatforms.includes(p) ? PC[p] : '#4b5563',
                border: `1px solid ${addPlatforms.includes(p) ? PC[p] + '50' : '#222'}`,
              }}>
              {PL[p]}
            </button>
          ))}
        </div>
        <Select value={String(addInterval)} onValueChange={v => setAddInterval(Number(v))}>
          <SelectTrigger className="h-7 text-[11px]" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
            {INTERVAL_OPTIONS.map(o => (
              <SelectItem key={o.value} value={String(o.value)} style={{ color: '#e5e7eb' }}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
          <input type="checkbox" checked={addEmailAlerts} onChange={e => setAddEmailAlerts(e.target.checked)}
            className="w-3 h-3 accent-[#00ff00]" />
          Email me on new activity
        </label>
        <Button size="sm" className="w-full h-7 text-xs"
          style={{ background: '#00ff0015', border: '1px solid #00ff0030', color: '#00ff00' }}
          disabled={!addKeyword.trim() || adding}
          onClick={submit}>
          <Plus size={11} className="mr-1" />
          {adding ? 'Adding...' : 'Add Stream'}
        </Button>
      </div>

      {platformBreakdown.length > 0 && (
        <div className="p-3 flex-shrink-0" style={{ borderTop: '1px solid #1a1a1a' }}>
          <p className="text-[10px] font-mono text-gray-600 uppercase tracking-wider mb-2">Signal sources</p>
          {platformBreakdown.map(([plat, count]) => (
            <div key={plat} className="flex items-center gap-2 mb-1.5">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PC[plat] ?? PC.default }} />
              <span className="text-[10px] text-gray-500 w-16 flex-shrink-0">{PL[plat] ?? plat}</span>
              <div className="flex-1 h-1 rounded-full bg-gray-900 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.round(count / Math.max(totalPosts, 1) * 100)}%`, background: PC[plat] ?? PC.default }} />
              </div>
              <span className="text-[10px] font-mono" style={{ color: PC[plat] ?? PC.default }}>{count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StreamSidebar;
